import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface RoleColorBadgeProps {
  name: ReactNode;
  color: string;
  className?: string;
  variant?: 'badge' | 'table';
  onClick?: () => void;
}

export function RoleColorBadge({
  name,
  color,
  className,
  variant = 'badge',
  onClick
}: RoleColorBadgeProps) {
  if (variant === 'table') {
    return (
      <button
        type='button'
        className={cn(
          'flex items-center gap-2.5 text-start',
          onClick && 'cursor-pointer hover:opacity-80',
          className
        )}
        onClick={onClick}
      >
        <span
          className='h-3 w-3 shrink-0 rounded-full'
          style={{ backgroundColor: color }}
        />
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold'>{name}</p>
          <p className='text-muted-foreground text-xs uppercase'>{color}</p>
        </div>
      </button>
    );
  }

  const Tag = onClick ? 'button' : 'span';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={cn(
        // `w-fit` because the badge is often a child of a flex-column
        // container (the permissions sheet header), where the default
        // `align-items: stretch` would otherwise pull it to full width.
        'text-foreground inline-flex w-fit max-w-full items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        onClick && 'cursor-pointer hover:opacity-80',
        className
      )}
      style={{
        // Only the tint and border carry the role colour. The label reads in
        // the theme's foreground: role colours are picked from a free colour
        // wheel, so a pale hex used as the text colour on a 12% tint of
        // itself was unreadable in both light and dark themes.
        backgroundColor: `${color}20`,
        border: `1px solid ${color}40`
      }}
      onClick={onClick}
    >
      <span
        className='h-2 w-2 shrink-0 rounded-full'
        style={{ backgroundColor: color }}
      />
      <span className='truncate'>{name}</span>
    </Tag>
  );
}
